import type { ChatMessage } from '#shared/types/chat';
// Uit components/admin/: die component is volledig los (geen enkele import) en
// heeft al een initialen-terugval met vaste kleur per naam. Een tweede
// avatarcomponent voor de publieke kant zou alleen maar uiteen gaan lopen.
import { Avatar } from '~/components/admin/Avatar';

// Eén bericht. Wordt door de bezoekerswidget én de agent-console gebruikt, dus
// alles komt via props: dit bestand kent geen van beide stores.

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

export interface ChatBubbleProps {
  message: ChatMessage;
  /** Is dit bericht van de kijker zelf? De bezoeker en de medewerker zien
   *  dezelfde berichten aan tegengestelde kanten. */
  own: boolean;
  /** Naam tonen? Alleen bij het eerste bericht van een reeks. */
  showName: boolean;
  /** Foto van de afzender, als die er is. Null of weglaten levert de initialen
   *  op — beter dan een gat, en het is het patroon dat het beheer al gebruikt. */
  avatarUrl?: string | null;
}

/** Breedte van de avatarkolom. Ook zonder foto blijft die staan, zodat
 *  opeenvolgende bellen van dezelfde afzender netjes onder elkaar uitlijnen in
 *  plaats van heen en weer te springen. */
const GUTTER = 28;

export function ChatBubble({ message, own, showName, avatarUrl }: ChatBubbleProps) {
  if (message.senderType === 'system') {
    return (
      <p className="self-center rounded-full bg-sunken px-3 py-1 text-center text-sm text-ink-3">
        {message.text}
      </p>
    );
  }

  const bubble = (
    <div className={`flex min-w-0 flex-col gap-0.5 ${own ? 'items-end' : 'items-start'}`}>
      {showName && !own && <span className="px-1 text-xs text-ink-3">{message.senderName}</span>}

      <div
        className={
          own
            ? // bg-accent-soft en NIET bg-accent. Drie redenen, waarvan de derde
              // beslissend: (1) een kolom massieve oranje blokken spreekt de
              // kleurhouding tegen — "het oranje zit in de inkt, niet in het
              // vlak" (theme.css:19-22); (2) accent-soft + accent-soft-ink haalt
              // 15,7:1 en is dezelfde paring als de actieve navigatiepil, dus de
              // chat oogt meteen eigen; (3) in forced-colors geeft theme.css:790
              // aan [class*="bg-accent-soft"] al een CanvasText-rand, waardoor de
              // twee beltypen onderscheidbaar blijven. Met bg-accent zouden ze
              // allebei naar Canvas inklappen en werd het gesprek onleesbaar.
              'rounded-2xl rounded-br-sm border border-hairline bg-accent-soft px-3.5 py-2 text-accent-soft-ink shadow-sm'
            : 'rounded-2xl rounded-bl-sm border border-hairline bg-surface px-3.5 py-2 text-ink-body shadow-sm'
        }
      >
        {message.mediaUrl && (
          // bg-sunken erachter, zodat een doorzichtige PNG niet verdwijnt op een
          // donkere ondergrond.
          <img
            src={message.mediaUrl}
            alt={`Afbeelding van ${message.senderName}`}
            width={message.mediaWidth ?? undefined}
            height={message.mediaHeight ?? undefined}
            className="mb-1.5 max-h-[220px] w-auto rounded-lg border border-hairline bg-sunken object-contain"
          />
        )}
        {/* Als TEKSTNODE, nooit dangerouslySetInnerHTML. Dit is onvertrouwde
            invoer van een anonieme bezoeker die óók in een beheerscherm wordt
            gerenderd — het zwaarste risico van de hele functie. */}
        {message.text && <p className="whitespace-pre-wrap break-words">{message.text}</p>}
      </div>

      {/* text-ink-3 en niet ink-4: theme.css:41-43 — "ink-400 is GEEN tekstkleur". */}
      <span className="px-1 text-xs text-ink-3">{time(message.createdAt)}</span>
    </div>
  );

  // Eigen berichten krijgen geen gezicht: je weet wie je zelf bent, en het zou
  // de halve breedte van het paneel opeten.
  if (own) return <div className="flex max-w-[85%] self-end">{bubble}</div>;

  return (
    <div className="flex max-w-[85%] items-start gap-2 self-start">
      <div style={{ width: GUTTER }} className="shrink-0">
        {showName && <Avatar person={{ username: message.senderName, avatar: avatarUrl }} size={GUTTER} />}
      </div>
      {bubble}
    </div>
  );
}
